"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import {
  ChevronLeft,
  ChevronRight,
  Grid3x3,
  Heart,
  List,
  Star,
  Trash2,
  X,
} from "lucide-react";

import {
  clearWishlist,
  deleteFromWishlist,
  getWishlist,
  type WishlistResponse,
} from "@/lib/api/business-api";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { usePageTranslation } from "@/hooks/usePageTranslation";
import { toIntlDateLocale, toIntlLocale } from "@/lib/i18n/intl-locale";
import { API_CONFIG } from "@/lib/config/api";
import { CachedImage } from "@/components/ui/CachedImage";
import { PLACEHOLDER_IMAGE_SVG } from "@/lib/placeholder-image";

interface WishlistViewProps {
  userData: any;
  businessId?: string;
}

const headingFontStyle = { fontFamily: "var(--brand-heading-font, inherit)" };

export function WishlistView({
  userData,
  businessId = API_CONFIG.DEFAULT_BUSINESS_ID,
}: WishlistViewProps) {
  const { t, currentLanguage } = usePageTranslation();
  const [items, setItems] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isClearing, setIsClearing] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "tiles">("tiles");
  const [deletingItems, setDeletingItems] = useState<Set<string>>(new Set());
  const wishlistTopRef = useRef<HTMLDivElement>(null);

  const perPage = 12;

  useEffect(() => {
    fetchWishlist(currentPage);
  }, [currentPage]);

  const fetchWishlist = async (page: number) => {
    try {
      setIsLoading(true);
      setError(null);

      const accessToken = userData.accessToken;
      if (!accessToken) {
        throw new Error(t("account.noAccessToken"));
      }

      const response: WishlistResponse = await getWishlist(
        accessToken,
        businessId,
        page,
        perPage
      );

      if (response.status && response.data) {
        setItems(response.data.items);
        setPagination(response.data.pagination);

        // Scroll to top of wishlist section after successful load
        if (wishlistTopRef.current) {
          wishlistTopRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      } else {
        throw new Error(t("account.fetchWishlistFailed"));
      }
    } catch (err: any) {
      console.error("Error fetching wishlist:", err);
      setError(err.message || t("account.loadWishlistFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveItem = async (productId: string) => {
    try {
      const accessToken = userData.accessToken;
      if (!accessToken) {
        throw new Error(t("account.noAccessToken"));
      }

      // Mark item as deleting
      setDeletingItems((prev) => new Set(prev).add(productId));

      // Delete from backend
      await deleteFromWishlist(accessToken, productId, businessId);

      // Only remove from state after successful deletion
      const updatedItems = items.filter((item) => item.id !== productId);
      setItems(updatedItems);

      // Update pagination total count
      if (pagination) {
        setPagination({
          ...pagination,
          total: pagination.total - 1,
        });
      }

      // Remove from deleting state
      setDeletingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    } catch (err: any) {
      console.error("Error removing from wishlist:", err);
      toast.error(t("account.removeWishlistFailed"));

      // Remove from deleting state on error
      setDeletingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  const handleClearWishlist = async () => {
    try {
      setIsClearing(true);
      const accessToken = userData.accessToken;
      if (!accessToken) {
        throw new Error(t("account.noAccessToken"));
      }

      await clearWishlist(accessToken, businessId);

      // Refresh the wishlist
      fetchWishlist(1);
      setCurrentPage(1);
    } catch (err: any) {
      console.error("Error clearing wishlist:", err);
      toast.error(t("account.clearWishlistFailed"));
    } finally {
      setIsClearing(false);
    }
  };

  const formatPrice = (price: number, currency: string = "GBP") => {
    return new Intl.NumberFormat(toIntlLocale(currentLanguage), {
      style: "currency",
      currency: currency,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(toIntlDateLocale(currentLanguage), {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const renderPagination = () => {
    if (!pagination || pagination.last_page <= 1) {
      return null;
    }

    const { current_page, last_page } = pagination;
    const pages: (number | string)[] = [];

    // Always show first page
    pages.push(1);

    // Show ellipsis or pages around current page
    if (current_page > 3) {
      pages.push("...");
    }

    // Show pages around current page
    for (
      let i = Math.max(2, current_page - 1);
      i <= Math.min(last_page - 1, current_page + 1);
      i++
    ) {
      pages.push(i);
    }

    // Show ellipsis or last page
    if (current_page < last_page - 2) {
      pages.push("...");
    }

    // Always show last page if there's more than one page
    if (last_page > 1) {
      pages.push(last_page);
    }

    return (
      <div className="mt-8 flex items-center justify-center gap-2">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          disabled={current_page === 1}
          className="rounded-brand border border-brand-border p-2 text-brand-muted hover:bg-brand-fg/5 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronLeft className="h-5 w-5 rtl:-scale-x-100" />
        </button>

        {pages.map((page, index) =>
          page === "..." ? (
            <span key={`ellipsis-${index}`} className="px-2 text-brand-muted">
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => setCurrentPage(page as number)}
              className={`rounded-brand px-4 py-2 ${
                current_page === page
                  ? "bg-brand-primary text-brand-primary-foreground"
                  : "border border-brand-border text-brand-muted hover:bg-brand-fg/5"
              }`}
            >
              {page}
            </button>
          )
        )}

        <button
          onClick={() =>
            setCurrentPage((prev) => Math.min(last_page, prev + 1))
          }
          disabled={current_page === last_page}
          className="rounded-brand border border-brand-border p-2 text-brand-muted hover:bg-brand-fg/5 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronRight className="h-5 w-5 rtl:-scale-x-100" />
        </button>
      </div>
    );
  };

  const sectionTitle = (
    <h2 className="text-2xl font-bold text-brand-fg" style={headingFontStyle}>
      {t("account.myWishlist")}
    </h2>
  );

  // Loading state
  if (isLoading && items.length === 0) {
    return (
      <div className="wishlist-section">
        <div className="mb-6">{sectionTitle}</div>
        <div className="flex items-center justify-center rounded-brand border border-brand-border bg-brand-surface p-12">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-brand-primary" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="wishlist-section">
        <div className="mb-6">{sectionTitle}</div>
        <div className="rounded-brand border border-brand-danger/30 bg-brand-danger/10 p-6 text-center">
          <X className="mx-auto mb-4 h-12 w-12 text-brand-danger" />
          <p className="mb-4 text-lg font-semibold text-brand-danger">{error}</p>
          <button
            onClick={() => fetchWishlist(currentPage)}
            className="rounded-brand bg-brand-danger px-6 py-2 font-semibold text-brand-danger-foreground transition-colors hover:bg-brand-danger/90"
          >
            {t("common.tryAgain")}
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!isLoading && items.length === 0) {
    return (
      <div className="wishlist-section">
        <div className="mb-6">{sectionTitle}</div>
        <div className="rounded-brand border border-brand-border bg-brand-fg/5 p-12 text-center">
          <Heart className="mx-auto mb-4 h-16 w-16 text-brand-muted" />
          <p className="mb-2 text-xl font-semibold text-brand-fg">
            {t("account.wishlistEmpty")}
          </p>
          <p className="mb-6 text-brand-muted">
            {t("account.wishlistEmptyMessage")}
          </p>
          <Link
            href="/"
            className="inline-block rounded-brand bg-brand-primary px-6 py-3 font-semibold text-brand-primary-foreground transition-colors hover:bg-brand-primary-hover"
          >
            {t("cart.startShopping")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="wishlist-section">
      <div ref={wishlistTopRef} className="mb-6 space-y-4">
        {/* Header with title and buttons */}
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            {sectionTitle}
            {pagination && (
              <p className="mt-1 text-sm text-brand-muted">
                {pagination.total === 1
                  ? t("account.itemCountOne")
                  : t("account.itemCount", { count: pagination.total })}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* View Toggle Buttons */}
            {items.length > 0 && (
              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`rounded-brand border-2 p-2 transition-all ${
                    viewMode === "grid"
                      ? "border-brand-primary bg-brand-primary text-brand-primary-foreground"
                      : "border-brand-border bg-brand-surface text-brand-fg hover:bg-brand-fg/5"
                  }`}
                  title={t("account.gridView")}
                >
                  <Grid3x3 className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setViewMode("tiles")}
                  className={`rounded-brand border-2 p-2 transition-all ${
                    viewMode === "tiles"
                      ? "border-brand-primary bg-brand-primary text-brand-primary-foreground"
                      : "border-brand-border bg-brand-surface text-brand-fg hover:bg-brand-fg/5"
                  }`}
                  title={t("account.tilesView")}
                >
                  <List className="h-5 w-5" />
                </button>
              </div>
            )}

            {/* Clear Wishlist Button */}
            {items.length > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    disabled={isClearing}
                    className="flex items-center gap-2 rounded-brand border-2 border-brand-danger bg-brand-surface px-4 py-2 font-semibold text-brand-danger transition-all hover:bg-brand-danger/10 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Trash2 className="h-5 w-5" />
                    {isClearing ? t("account.clearing") : t("account.clearWishlist")}
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="max-w-[90vw] sm:max-w-lg">
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("account.clearWishlist")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("account.clearWishlistConfirm")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                    <AlertDialogAction onClick={handleClearWishlist}>
                      {t("account.clearWishlist")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </div>

      {/* Wishlist Grid View */}
      {viewMode === "grid" && (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((item) => {
            const isDeleting = deletingItems.has(item.id);
            return (
              <div
                key={item.id}
                className={`group relative flex flex-col overflow-hidden rounded-brand-lg border border-brand-border bg-brand-surface shadow-sm transition-all ${
                  isDeleting
                    ? "pointer-events-none opacity-60"
                    : "hover:shadow-lg"
                }`}
              >
                {/* Remove button */}
                <button
                  onClick={() => handleRemoveItem(item.id)}
                  disabled={isDeleting}
                  className="absolute top-2 end-2 z-10 rounded-full bg-brand-surface p-2 text-brand-danger shadow-md transition-all hover:scale-110 hover:bg-brand-danger/10 disabled:cursor-not-allowed disabled:opacity-50"
                  title={
                    isDeleting
                      ? t("account.removing")
                      : t("account.removeFromWishlist")
                  }
                >
                  {isDeleting ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-danger border-t-transparent" />
                  ) : (
                    <X className="h-4 w-4" />
                  )}
                </button>

                {/* Product Image */}
                <Link href={`/${item.slug}`} className="block">
                  <div className="relative aspect-square w-full overflow-hidden bg-brand-fg/5">
                    <CachedImage
                      src={item.image}
                      alt={`${item.name}${item.brand ? ` - ${item.brand}` : ''}`}
                      fill
                      className="object-cover transition-transform group-hover:scale-105"
                      sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                      cacheKey={`wishlist-grid-${item.product_id}`}
                      fallbackSrc={PLACEHOLDER_IMAGE_SVG}
                    />
                    {!item.in_stock && (
                      <div className="bg-opacity-20 absolute inset-0 flex items-center justify-center bg-black">
                        <span className="rounded-brand bg-brand-danger px-4 py-2 text-sm font-semibold text-brand-danger-foreground">
                          {t("product.outOfStock")}
                        </span>
                      </div>
                    )}
                  </div>
                </Link>

                {/* Product Info - flex-grow to push button to bottom */}
                <div className="flex flex-1 flex-col p-4">
                  <Link
                    href={`/${item.slug}`}
                    className="block transition-colors hover:text-brand-primary"
                  >
                    <p className="mb-1 text-sm text-brand-muted">{item.brand}</p>
                    <h3 className="mb-2 line-clamp-2 font-semibold text-brand-fg">
                      {item.name}
                    </h3>
                  </Link>

                  {/* Rating */}
                  {item.rating > 0 && (
                    <div className="mb-2 flex items-center gap-1">
                      <Star className="h-4 w-4 fill-brand-warning text-brand-warning" />
                      <span className="text-sm font-medium text-brand-fg">
                        {item.rating.toFixed(1)}
                      </span>
                    </div>
                  )}

                  {/* Price */}
                  <div className="mb-3">
                    <span className="text-lg font-bold text-brand-primary">
                      {formatPrice(item.price, item.currency)}
                    </span>
                  </div>

                  {/* Added date */}
                  <p className="mb-3 text-xs text-brand-muted">
                    {t("account.addedOn", { date: formatDate(item.added_at) })}
                  </p>

                  {/* View Product Button - mt-auto pushes it to bottom */}
                  <Link
                    href={`/${item.slug}`}
                    className="mt-auto block w-full rounded-brand bg-brand-primary py-2 text-center font-semibold text-brand-primary-foreground transition-colors hover:bg-brand-primary-hover"
                  >
                    {t("account.viewProduct")}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Wishlist Tiles View */}
      {viewMode === "tiles" && (
        <div className="space-y-4">
          {items.map((item) => {
            const isDeleting = deletingItems.has(item.id);
            return (
              <Link
                key={item.id}
                href={`/${item.slug}`}
                className={`group relative flex gap-4 overflow-hidden rounded-brand-lg border border-brand-border bg-brand-surface p-4 shadow-sm transition-all ${
                  isDeleting
                    ? "pointer-events-none opacity-60"
                    : "hover:shadow-lg"
                }`}
              >
                {/* Remove button */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemoveItem(item.id);
                  }}
                  disabled={isDeleting}
                  className="absolute top-2 end-2 z-10 rounded-full bg-brand-surface p-2 text-brand-danger shadow-md transition-all hover:scale-110 hover:bg-brand-danger/10 disabled:cursor-not-allowed disabled:opacity-50"
                  title={
                    isDeleting
                      ? t("account.removing")
                      : t("account.removeFromWishlist")
                  }
                >
                  {isDeleting ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-danger border-t-transparent" />
                  ) : (
                    <X className="h-4 w-4" />
                  )}
                </button>

                {/* Product Image */}
                <div className="flex-shrink-0">
                  <div className="relative h-32 w-32 overflow-hidden rounded-brand bg-brand-fg/5">
                    <CachedImage
                      src={item.image}
                      alt={`${item.name}${item.brand ? ` - ${item.brand}` : ''}`}
                      fill
                      className="object-cover transition-transform group-hover:scale-105"
                      sizes="128px"
                      cacheKey={`wishlist-list-${item.product_id}`}
                      fallbackSrc={PLACEHOLDER_IMAGE_SVG}
                    />
                    {!item.in_stock && (
                      <div className="bg-opacity-20 absolute inset-0 flex items-center justify-center bg-black">
                        <span className="rounded-brand-sm bg-brand-danger px-2 py-1 text-xs font-semibold text-brand-danger-foreground">
                          {t("product.outOfStock")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Product Info */}
                <div className="flex flex-1 flex-col justify-between">
                  <div>
                    <p className="mb-1 text-sm text-brand-muted">{item.brand}</p>
                    <h3 className="mb-2 line-clamp-2 text-lg font-semibold text-brand-fg transition-colors group-hover:text-brand-primary">
                      {item.name}
                    </h3>

                    <div className="flex items-center gap-4">
                      {/* Rating */}
                      {item.rating > 0 && (
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 fill-brand-warning text-brand-warning" />
                          <span className="text-sm font-medium text-brand-fg">
                            {item.rating.toFixed(1)}
                          </span>
                        </div>
                      )}

                      {/* Price */}
                      <span className="text-xl font-bold text-brand-primary">
                        {formatPrice(item.price, item.currency)}
                      </span>

                      {/* Added date */}
                      <p className="text-xs text-brand-muted">
                        {t("account.addedOn", {
                          date: formatDate(item.added_at),
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {renderPagination()}

      {/* Loading Indicator */}
      {isLoading && items.length > 0 && (
        <div className="mt-4 flex items-center justify-center rounded-brand border border-brand-primary/30 bg-brand-primary/5 p-4">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-primary" />
          <span className="ms-3 text-sm font-medium text-brand-primary">
            {t("account.loadingWishlist")}
          </span>
        </div>
      )}
    </div>
  );
}
